import React, { useEffect, useState } from 'react';
import { ArrowRight, CheckCircle2, HelpCircle, XIcon, CalendarClock, Target, Users, DollarSign } from 'lucide-react';
import { Button } from './Button';
interface JoinPilotModalProps {
  isOpen: boolean;
  onClose: () => void;
  origin: {
    x: number;
    y: number;
  } | null;
}
interface FormData {
  fullName: string;
  email: string;
  city: string;
  state: string;
  schools: string;
  coachingCapacity: {
    schoolCoach: boolean;
    districtCoach: boolean;
    coachingProviderEmployee: boolean;
    contractor: boolean;
    administrator: boolean;
    other: boolean;
    otherText: string;
  };
  subjectsAndGrades: string;
  curricula: {
    illustrativeMath: boolean;
    eurekaMath: boolean;
    other: boolean;
    otherText: string;
  };
  teachingLabRelationship: {
    fullTimeEmployee: boolean;
    partTimeFacilitator: boolean;
    tabMember: boolean;
    notAffiliated: boolean;
  };
  hearAboutNisa: string;
  whyInterested: string;
}
export const JoinPilotModal: React.FC<JoinPilotModalProps> = ({
  isOpen,
  onClose,
  origin
}) => {
  const [animationComplete, setAnimationComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    city: '',
    state: '',
    schools: '',
    coachingCapacity: {
      schoolCoach: false,
      districtCoach: false,
      coachingProviderEmployee: false,
      contractor: false,
      administrator: false,
      other: false,
      otherText: ''
    },
    subjectsAndGrades: '',
    curricula: {
      illustrativeMath: false,
      eurekaMath: false,
      other: false,
      otherText: ''
    },
    teachingLabRelationship: {
      fullTimeEmployee: false,
      partTimeFacilitator: false,
      tabMember: false,
      notAffiliated: false
    },
    hearAboutNisa: '',
    whyInterested: ''
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      const checked = checkbox.checked;
      if (name.startsWith('curricula.')) {
        const curriculaField = name.split('.')[1];
        setFormData(prev => ({
          ...prev,
          curricula: {
            ...prev.curricula,
            [curriculaField]: checked
          }
        }));
      } else if (name.startsWith('teachingLabRelationship.')) {
        const relationshipField = name.split('.')[1];
        setFormData(prev => ({
          ...prev,
          teachingLabRelationship: {
            ...prev.teachingLabRelationship,
            [relationshipField]: checked
          }
        }));
      } else if (name.startsWith('coachingCapacity.')) {
        const capacityField = name.split('.')[1];
        setFormData(prev => ({
          ...prev,
          coachingCapacity: {
            ...prev.coachingCapacity,
            [capacityField]: checked
          }
        }));
      }
    } else {
      // Handle text inputs and textareas
      if (name === 'curricula.otherText') {
        setFormData(prev => ({
          ...prev,
          curricula: {
            ...prev.curricula,
            otherText: value
          }
        }));
      } else if (name === 'coachingCapacity.otherText') {
        setFormData(prev => ({
          ...prev,
          coachingCapacity: {
            ...prev.coachingCapacity,
            otherText: value
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 1500);
  };
  useEffect(() => {
    if (isOpen) {
      // Reset animation state when modal opens
      setAnimationComplete(false);
      // Mark animation as complete after transition ends
      const timer = setTimeout(() => {
        setAnimationComplete(true);
      }, 400); // Match this with the CSS transition duration
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  // Calculate transform origin based on button position
  const getTransformOriginStyle = () => {
    if (!origin) return {};
    // Convert absolute position to percentage of viewport
    const originX = origin.x / window.innerWidth * 100;
    const originY = origin.y / window.innerHeight * 100;
    return {
      transformOrigin: `${originX}% ${originY}%`
    };
  };
  return <div className={`fixed inset-0 z-50 overflow-y-auto bg-white transition-all duration-400 ease-out ${animationComplete ? '' : 'scale-enter'}`} style={{
    ...getTransformOriginStyle()
  }} data-id="element-192">
      <style jsx global data-id="element-193">{`
        .scale-enter {
          transform: scale(0.1);
          opacity: 0;
        }
        @keyframes rippleEffect {
          0% {
            box-shadow: 0 0 0 0 rgba(0, 122, 143, 0.3);
          }
          100% {
            box-shadow: 0 0 0 100vmax rgba(0, 122, 143, 0);
          }
        }
      `}</style>
      <div className="absolute top-4 right-4 z-50" data-id="element-194">
        <button onClick={onClose} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center" aria-label="Close modal" data-id="element-195">
          <XIcon className="w-6 h-6 text-gray-600" data-id="element-196" />
        </button>
      </div>
      <main className="relative min-h-screen bg-gradient-to-r from-white via-primary-50/40 to-blue-50/30 overflow-hidden" data-id="element-197">
        {/* Background gradient blobs */}
        <div className="absolute -top-40 -right-20 w-[600px] h-[500px] bg-gradient-to-br from-orange-400/20 via-red-400/20 to-yellow-400/20 rounded-[60%_40%_55%_45%] blur-3xl transform rotate-12 animate-float" data-id="element-198"></div>
        <div className="absolute -bottom-20 -left-20 w-[500px] h-[600px] bg-gradient-to-tr from-purple-500/20 via-violet-400/20 to-fuchsia-400/20 rounded-[40%_60%_45%_55%] blur-3xl transform -rotate-12 animate-float" style={{
        animationDelay: '2s'
      }} data-id="element-199"></div>
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] z-0" data-id="element-200" />
        {/* Header section */}
        <section className="w-full pt-12 pb-3 md:pt-16 md:pb-4 relative z-10" data-id="element-201">
          <div className="container mx-auto px-4 sm:px-6 relative" data-id="element-202">
            <div className="max-w-4xl mx-auto" data-id="element-203">
              <div id="hero-logo" className="mb-4 text-center" data-id="element-204">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jP7ueorRtM9x7giMYpBsys/nisa-capriola-logotype.png" alt="Nisa" className="h-10 md:h-14 w-auto mx-auto" data-id="element-205" />
              </div>
              <div className="mb-4" data-id="element-206">
                <h1 className="text-2xl md:text-3xl font-serif font-normal text-gray-900 mb-4 text-center pt-5" data-id="element-207">
                  Join the Nisa coaching pilot
                </h1>
                <p className="text-base md:text-lg text-gray-700 px-4" data-id="element-208">
                  Nisa is a platform for instructional coaches to extend
                  professional learning between coaching sessions. Our AI tools
                  lighten your workload and strengthen coach–teacher
                  relationships.
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* Content section */}
        <section className="w-full py-8 md:py-12 relative z-10" data-id="element-209">
          {/* XL Gradient Blobs - Background Layer */}
          <div className="absolute -top-[400px] -right-[300px] w-[1000px] h-[1000px] bg-gradient-to-br from-blue-400/10 via-cyan-400/10 to-teal-400/10 rounded-[55%_45%_60%_40%] blur-3xl animate-float z-0" data-id="element-210"></div>
          <div className="absolute -bottom-[300px] -left-[200px] w-[900px] h-[900px] bg-gradient-to-tr from-purple-400/10 via-violet-400/10 to-fuchsia-400/10 rounded-[40%_60%_55%_45%] blur-3xl animate-float z-0" style={{
          animationDelay: '2s'
        }} data-id="element-211"></div>
          <div className="absolute top-[10%] left-[30%] w-[800px] h-[800px] bg-gradient-to-r from-amber-400/10 via-orange-400/10 to-pink-400/10 rounded-[45%_55%_40%_60%] blur-3xl animate-float z-0" style={{
          animationDelay: '3.5s'
        }} data-id="element-212"></div>
          <div className="absolute bottom-[20%] right-[20%] w-[700px] h-[700px] bg-gradient-to-l from-emerald-400/10 via-green-400/10 to-lime-400/10 rounded-[60%_40%_50%_50%] blur-3xl animate-float z-0" style={{
          animationDelay: '1.5s'
        }} data-id="element-213"></div>
          <div className="container mx-auto px-4 sm:px-6 relative z-10" data-id="element-214">
            <div className="max-w-4xl mx-auto" data-id="element-215">
              <div className="mb-8" data-id="element-216">
                {/* Combined content in a single container */}
                <div className="bg-white/90 backdrop-blur-sm rounded-xl py-5 px-9 shadow-sm border border-gray-100" data-id="element-217">
                  <h3 className="text-lg md:text-xl font-sans font-medium text-gray-800 mb-3" data-id="element-218">
                    Now recruiting for the first coach cohort
                  </h3>
                  <p className="text-sm text-gray-700 mb-6" data-id="element-219">
                    We're building a group of US-based instructional coaches,
                    currently working in K-12 schools. Coaches can join at any
                    point in the school year.
                  </p>
                  <div className="border-none border-gray-100 pt-4 mt-4" data-id="element-220">
                    <h3 className="text-lg md:text-xl font-sans font-medium text-gray-800 mb-3" data-id="element-221">
                      Benefits for pilot coaches
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-id="element-222">
                      <div className="bg-blue-50/70 rounded-lg p-3 border border-blue-100/50 hover:shadow-sm transition-shadow" data-id="element-223">
                        <div className="flex items-center" data-id="element-224">
                          <div className="bg-blue-100 rounded-full p-1.5 mr-3 flex-shrink-0" data-id="element-225">
                            <CalendarClock className="w-4 h-4 text-blue-700" data-id="element-226" />
                          </div>
                          <span className="text-sm text-gray-800" data-id="element-227">
                            Get early access at no cost for '25–'26
                          </span>
                        </div>
                      </div>
                      <div className="bg-cyan-50/70 rounded-lg p-3 border border-cyan-100/50 hover:shadow-sm transition-shadow" data-id="element-228">
                        <div className="flex items-center" data-id="element-229">
                          <div className="bg-cyan-100 rounded-full p-1.5 mr-3 flex-shrink-0" data-id="element-230">
                            <DollarSign className="w-4 h-4 text-cyan-700" data-id="element-231" />
                          </div>
                          <span className="text-sm text-gray-800" data-id="element-232">
                            Access paid feedback opportunities
                          </span>
                        </div>
                      </div>
                      <div className="bg-primary-50/70 rounded-lg p-3 border border-primary-100/50 hover:shadow-sm transition-shadow" data-id="element-233">
                        <div className="flex items-center" data-id="element-234">
                          <div className="bg-primary-100 rounded-full p-1.5 mr-3 flex-shrink-0" data-id="element-235">
                            <Users className="w-4 h-4 text-primary-700" data-id="element-236" />
                          </div>
                          <span className="text-sm text-gray-800" data-id="element-237">
                            Receive support and connect with other coaches
                          </span>
                        </div>
                      </div>
                      <div className="bg-purple-50/70 rounded-lg p-3 border border-purple-100/50 hover:shadow-sm transition-shadow" data-id="element-238">
                        <div className="flex items-center" data-id="element-239">
                          <div className="bg-purple-100 rounded-full p-1.5 mr-3 flex-shrink-0" data-id="element-240">
                            <Target className="w-4 h-4 text-purple-700" data-id="element-241" />
                          </div>
                          <span className="text-sm text-gray-800" data-id="element-242">
                            Shape the future of coaching tools
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Application Form Section */}
              <div className="mb-12" data-id="element-243">
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-6 md:p-8 shadow-sm border border-gray-100" data-id="element-244">
                  {isSubmitted ? <div className="py-8" data-id="element-245">
                      <div className="flex items-center justify-center w-20 h-20 rounded-full bg-green-100 text-green-700 mb-6 mx-auto" data-id="element-246">
                        <CheckCircle2 className="w-10 h-10" data-id="element-247" />
                      </div>
                      <h3 className="text-2xl font-serif font-normal text-gray-900 mb-4 text-center" data-id="element-248">
                        Thank you for applying!
                      </h3>
                      <p className="text-lg text-gray-700 text-center" data-id="element-249">
                        We've received your application for the Nisa coaching
                        pilot. We'll be in touch with you soon about next steps.
                      </p>
                    </div> : <>
                      <h3 className="text-xl md:text-xl font-sans font-medium text-gray-800 mb-4" data-id="element-250">
                        Tell us about yourself
                      </h3>
                      <p className="text-base text-gray-700 mb-6" data-id="element-251">
                        Please complete the form below. We'll review your
                        application promptly and be in touch soon. If you have
                        any questions, feel free to contact{' '}
                        <a href="mailto:info@nisa.coach" className="text-primary-700 hover:text-primary-800 font-medium" data-id="element-252">
                          info@nisa.coach
                        </a>
                      </p>
                      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-253">
                        {/* Full Name Field */}
                        <div className="form-group" data-id="element-254">
                          <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-255">
                            Full Name *
                          </label>
                          <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleInputChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" data-id="element-256" />
                        </div>
                        {/* Email Address Field */}
                        <div className="form-group" data-id="element-257">
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-258">
                            Your Email (Google accounts only) *
                          </label>
                          <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" placeholder="yourname@gmail.com" data-id="element-259" />
                          <p className="text-xs text-gray-600 mt-1" data-id="element-260">
                            Pilot accounts require login via Google email.
                          </p>
                        </div>
                        {/* Location Field */}
                        <div className="form-group" data-id="element-261">
                          <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-262">
                            Where are you based?
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-263">
                            <div data-id="element-264">
                              <input type="text" id="city" name="city" placeholder="City" value={formData.city} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" data-id="element-265" />
                            </div>
                            <div data-id="element-266">
                              <input type="text" id="state" name="state" placeholder="State" value={formData.state} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" data-id="element-267" />
                            </div>
                          </div>
                        </div>
                        {/* Schools Field */}
                        <div className="form-group" data-id="element-268">
                          <label htmlFor="schools" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-269">
                            Which school(s) do you currently coach?
                          </label>
                          <input type="text" id="schools" name="schools" value={formData.schools} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" data-id="element-270" />
                        </div>
                        {/* Coaching Capacity Field */}
                        <div className="form-group" data-id="element-271">
                          <label htmlFor="coachingCapacity" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-272">
                            How do you serve as a coach at these schools?
                          </label>
                          <div className="space-y-2 mt-2" data-id="element-273">
                            <div className="flex items-start" data-id="element-274">
                              <input type="checkbox" id="schoolCoach" name="coachingCapacity.schoolCoach" checked={formData.coachingCapacity.schoolCoach} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-275" />
                              <label htmlFor="schoolCoach" className="text-sm text-gray-700" data-id="element-276">
                                School Coach
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-277">
                              <input type="checkbox" id="districtCoach" name="coachingCapacity.districtCoach" checked={formData.coachingCapacity.districtCoach} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-278" />
                              <label htmlFor="districtCoach" className="text-sm text-gray-700" data-id="element-279">
                                District Coach
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-280">
                              <input type="checkbox" id="coachingProviderEmployee" name="coachingCapacity.coachingProviderEmployee" checked={formData.coachingCapacity.coachingProviderEmployee} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-281" />
                              <label htmlFor="coachingProviderEmployee" className="text-sm text-gray-700" data-id="element-282">
                                Coaching Provider Employee
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-283">
                              <input type="checkbox" id="contractor" name="coachingCapacity.contractor" checked={formData.coachingCapacity.contractor} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-284" />
                              <label htmlFor="contractor" className="text-sm text-gray-700" data-id="element-285">
                                Contractor
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-286">
                              <input type="checkbox" id="administrator" name="coachingCapacity.administrator" checked={formData.coachingCapacity.administrator} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-287" />
                              <label htmlFor="administrator" className="text-sm text-gray-700" data-id="element-288">
                                Administrator
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-289">
                              <input type="checkbox" id="otherCapacity" name="coachingCapacity.other" checked={formData.coachingCapacity.other} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-290" />
                              <label htmlFor="otherCapacity" className="text-sm text-gray-700" data-id="element-291">
                                Other
                              </label>
                              {formData.coachingCapacity.other && <input type="text" id="otherCapacityText" name="coachingCapacity.otherText" value={formData.coachingCapacity.otherText} onChange={handleInputChange} placeholder="Please specify" className="ml-2 px-3 py-1 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" data-id="element-292" />}
                            </div>
                          </div>
                        </div>
                        {/* Subjects and Grades Field */}
                        <div className="form-group" data-id="element-293">
                          <label htmlFor="subjectsAndGrades" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-294">
                            What subjects and grade levels do you coach?
                          </label>
                          <input type="text" id="subjectsAndGrades" name="subjectsAndGrades" value={formData.subjectsAndGrades} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" data-id="element-295" />
                        </div>
                        {/* Curriculum Field */}
                        <div className="form-group" data-id="element-296">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-297">
                            Which curriculum do you support teachers with?
                          </label>
                          <div className="space-y-2 mt-2" data-id="element-298">
                            <div className="flex items-start" data-id="element-299">
                              <input type="checkbox" id="illustrativeMath" name="curricula.illustrativeMath" checked={formData.curricula.illustrativeMath} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-300" />
                              <label htmlFor="illustrativeMath" className="text-sm text-gray-700" data-id="element-301">
                                Illustrative Mathematics
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-302">
                              <input type="checkbox" id="eurekaMath" name="curricula.eurekaMath" checked={formData.curricula.eurekaMath} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-303" />
                              <label htmlFor="eurekaMath" className="text-sm text-gray-700" data-id="element-304">
                                Eureka Math
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-305">
                              <input type="checkbox" id="otherCurricula" name="curricula.other" checked={formData.curricula.other} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-306" />
                              <label htmlFor="otherCurricula" className="text-sm text-gray-700" data-id="element-307">
                                Other
                              </label>
                            </div>
                            {formData.curricula.other && <div className="ml-6" data-id="element-308">
                                <input type="text" id="otherCurriculaText" name="curricula.otherText" value={formData.curricula.otherText} onChange={handleInputChange} placeholder="Please specify" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" data-id="element-309" />
                              </div>}
                          </div>
                        </div>
                        {/* Teaching Lab Relationship Field */}
                        <div className="form-group" data-id="element-310">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-311">
                            What is your relationship with Teaching Lab? *
                          </label>
                          <p className="text-xs text-gray-700 mb-2" data-id="element-312">
                            Select all that apply. No affiliation is required to
                            join our pilot.
                          </p>
                          <div className="space-y-2 mt-2" data-id="element-313">
                            <div className="flex items-start" data-id="element-314">
                              <input type="checkbox" id="fullTimeEmployee" name="teachingLabRelationship.fullTimeEmployee" checked={formData.teachingLabRelationship.fullTimeEmployee} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-315" />
                              <label htmlFor="fullTimeEmployee" className="text-sm text-gray-700" data-id="element-316">
                                Full-time Teaching Lab employee (including
                                coaches)
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-317">
                              <input type="checkbox" id="partTimeFacilitator" name="teachingLabRelationship.partTimeFacilitator" checked={formData.teachingLabRelationship.partTimeFacilitator} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-318" />
                              <label htmlFor="partTimeFacilitator" className="text-sm text-gray-700" data-id="element-319">
                                Part-time Teaching Lab facilitator and/or coach
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-320">
                              <input type="checkbox" id="tabMember" name="teachingLabRelationship.tabMember" checked={formData.teachingLabRelationship.tabMember} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-321" />
                              <label htmlFor="tabMember" className="text-sm text-gray-700" data-id="element-322">
                                Teacher Advisory Board (TAB) member
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-323">
                              <input type="checkbox" id="notAffiliated" name="teachingLabRelationship.notAffiliated" checked={formData.teachingLabRelationship.notAffiliated} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-324" />
                              <label htmlFor="notAffiliated" className="text-sm text-gray-700" data-id="element-325">
                                Not affiliated with Teaching Lab
                              </label>
                            </div>
                          </div>
                        </div>
                        {/* How did you discover Nisa Field */}
                        <div className="form-group" data-id="element-326">
                          <label htmlFor="hearAboutNisa" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-327">
                            How did you discover Nisa?
                          </label>
                          <input type="text" id="hearAboutNisa" name="hearAboutNisa" value={formData.hearAboutNisa} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" data-id="element-328" />
                        </div>
                        {/* Why interested Field */}
                        <div className="form-group" data-id="element-329">
                          <label htmlFor="whyInterested" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-330">
                            Why are you interested in joining our pilot program?
                          </label>
                          <div className="relative" data-id="element-331">
                            <textarea id="whyInterested" name="whyInterested" value={formData.whyInterested} onChange={handleInputChange} rows={4} maxLength={500} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 pb-8" data-id="element-332"></textarea>
                            <div className="absolute bottom-2 right-3 text-xs text-gray-600" data-id="element-333">
                              {formData.whyInterested.length}/500
                            </div>
                          </div>
                        </div>
                        {/* Submit Button */}
                        <div className="pt-2 flex justify-center" data-id="element-334">
                          <Button type="submit" size="lg" fullWidth disabled={isSubmitting} data-id="element-335">
                            {isSubmitting ? <span className="flex items-center justify-center" data-id="element-336">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-337">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-338"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-339"></path>
                                </svg>
                                Submitting...
                              </span> : <>
                                Submit Your Application
                                <ArrowRight className="w-5 h-5 ml-2" data-id="element-340" />
                              </>}
                          </Button>
                        </div>
                        {/* Form Footer */}
                        <p className="text-xs text-gray-700 mt-4 text-center" data-id="element-341">
                          By submitting this form, you agree to be contacted
                          about the Nisa coaching pilot.
                          <br data-id="element-342" />
                          We respect your privacy. Your info will never be
                          shared with third parties.
                        </p>
                      </form>
                    </>}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>;
};